import debounce from "lodash/debounce";
import { apiGetSignalsData } from "@/Groups/Signals/private/api/signalsData";
import { getLastSignals } from "@/Groups/Signals/private/api/lastSignals";
import { apiPinSignal } from "@/Groups/Signals/private/api/pinSignal";
import { apiGetTheSignal } from "@/Groups/Signals/private/api/apiGetTheSignal";
import { toggleFavouritesSignal } from "@/Groups/Signals/private/api/toggleFavouritesSignal";
import { apiSendSignalToServer, apiSendChangedSignalToServer } from "@/Groups/Signals/private/api/sendSignalToServer";
import { apiGetSignalsFiltered } from "@/Groups/Signals/private/api/apiGetSignalsFiltered";

const Signals = {
  namespaced: true,

  state: { 
    data: {},
    map: [],
    next: null,

    isLoading: false,
    isReady: false,
    isLoadingNext: false,

    optimisticOriginal: {},
    debounceTimers: {},

    lastSignals: [],
    isLoadingLastSignals: false,
    readyLastSignals: false,

    draftForm: {},
    idEditSignal: null,
    isSignalSendLoading: false,

    activeFilter: null,
  },

  actions: {
    // -------------------------------
    // ИНИЦИАЛИЗАЦИЯ
    // -------------------------------
    async initSignals({ state, commit, dispatch }) {
      await dispatch("getSignals");

      if (!Object.keys(state.data).length) return;

      commit("SET_READY_FLAG");
      commit("SAVE_FAVORITES_COUNT_TO_STORAGE");
    },

    // -------------------------------
    // ПОЛУЧЕНИЕ ВСЕХ СИГНАЛОВ
    // -------------------------------
    async getSignals({ commit }) {
      commit("TOGGLE_LOADING_FLAG", true);
      try {
        const data = await apiGetSignalsData();

        commit("CLEAR_DATA");
        commit("SET_DATA", data.results);
        commit("SET_NEW_LINK", data.next);
        commit("SORT_SIGNALS");
      } catch (err) {
        console.error("Ошибка при получении сигналов:", err);
      } finally {
        commit("TOGGLE_LOADING_FLAG", false);
      }
    },

    async applyFilterWithToggle({ state, dispatch }, filterName) {
      if (state.activeFilter === filterName) {
        state.activeFilter = null;
        await dispatch("getSignals");
        return;
      }

      state.activeFilter = filterName;
      await dispatch("applyFilter", filterName);
    },

    async applyFilter({ commit }, filterName) {
      commit("TOGGLE_LOADING_FLAG", true);

      try {
        const data = await apiGetSignalsFiltered(filterName);

        commit("CLEAR_DATA");
        commit("SET_DATA", data.results);
        commit("SORT_SIGNALS");
        commit("SET_NEW_LINK", data.next || null);
      } catch (err) {
        console.error("Ошибка фильтрации:", err);
      } finally {
        commit("TOGGLE_LOADING_FLAG", false);
      }
    },

    //Сбросить фильтры
    async resetFilter({ commit, state }) {
      if (state.activeFilter === null) return;
      state.activeFilter = null;
      commit('TOGGLE_LOADING_FLAG', true);
      try {
        const data = await apiGetSignalsData();
        commit('CLEAR_DATA');
        commit('SET_DATA', data.results);
        commit('SORT_SIGNALS');
        commit('SET_NEW_LINK', data.next);
      } catch (err) {
        console.error('Ошибка сброса фильтров:', err);
      } finally {
        commit('TOGGLE_LOADING_FLAG', false);
      }
    },
    
    // -------------------------------
    // ДОГРУЗКА СЛЕДУЮЩЕЙ СТРАНИЦЫ
    // -------------------------------
    async loadNextSignals({ commit, state }) {
      if (!state.next) return;

      commit("TOGGLE_LOADING_NEW_PAGE_FLAG", true);
      try {
        const data = await apiGetSignalsData(state.next);
        commit("SET_DATA", data.results);
        commit("SET_NEW_LINK", data.next);
      } catch (err) {
        console.error("Ошибка получения следующей страницы:", err);
      } finally {
        commit("TOGGLE_LOADING_NEW_PAGE_FLAG", false);
      }
    },

    // -------------------------------
    // ПОЛУЧИТЬ И ДОБАВИТЬ СИГНАЛ
    // -------------------------------
    async getAndAddTheSignal({ commit }, id) {
      try {
        const data = await apiGetTheSignal(id);
        commit("SET_NEW_SIGNAL", data);
        return data;
      } catch (err) {
        console.error("Ошибка при получении сигнала:", err);
        return Promise.reject(err);
      }
    },

    // -------------------------------
    // ИЗБРАННОЕ (оптимистичное)
    // -------------------------------
    toggleFavouriteOptimistic({ state, commit }, id) {
      const signal = state.data[id];
      if (!signal) return;

      const currentValue = signal.reactions?.bookmarked?.is_mine ?? false;

      // Сохраняем оригинальное значение только один раз
      if (!(id in state.optimisticOriginal)) {
        state.optimisticOriginal[id] = currentValue;
      }

      const newValue = !currentValue;

      commit("SET_BOOKMARK_STATE", { id, value: newValue });

      // ❗ localStorage НЕ ОБНОВЛЯЕМ до факта успеха
      // (это ключевая причина багов, теперь исправлено)

      if (!state.debounceTimers[id]) {
        state.debounceTimers[id] = debounce(async () => {
          try {
            await toggleFavouritesSignal({
              object_id: id,
              content_type: signal.content_type,
            });

            delete state.optimisticOriginal[id];

            // Обновляем localStorage только ПОСЛЕ успеха
            commit("SAVE_FAVORITES_COUNT_TO_STORAGE");
          } catch (err) {
            console.error("Ошибка сервера, откат:", err);

            commit("SET_BOOKMARK_STATE", {
              id,
              value: state.optimisticOriginal[id],
            });

            delete state.optimisticOriginal[id];

            // После отката → обновляем LS правильным значением
            commit("SAVE_FAVORITES_COUNT_TO_STORAGE");
          } finally {
            delete state.debounceTimers[id];
          }
        }, 800);
      }

      state.debounceTimers[id]();
    },

    // -------------------------------
    // ПОСЛЕДНИЕ СИГНАЛЫ
    // -------------------------------
    async getLastSignals({ commit }) {
      commit("SET_LOADING_LAST_SIGNALS", true);

      try {
        const data = await getLastSignals();
        commit("SET_LAST_SIGNALS", data);
        commit("SET_READY_LAST_SIGNALS", true);
        return data;
      } catch (err) {
        console.error("Ошибка получения lastSignals:", err);
        return Promise.reject(err);
      } finally {
        commit("SET_LOADING_LAST_SIGNALS", false);
      }
    },

    // -------------------------------
    // PIN / UNPIN
    // -------------------------------
    async pinSignal({ commit, state }, { id }) {
      try {
        const methodType = state.data[id].pinned ? "DELETE" : "POST";
        const data = await apiPinSignal({ id, methodType });

        commit("PIN_SIGNAL", id);
        commit("SORT_SIGNALS");

        return data;
      } catch (err) {
        console.error("Ошибка pin/unpin:", err);
        return Promise.reject(err);
      }
    },

    // -------------------------------
    // ДОБАВИТЬ НОВЫЙ СИГНАЛ
    // -------------------------------
    async addSignal({ commit }, formData) {
      commit("TOGGLE_SIGNAL_SEND_LOADING_FLAG", true);

      try {
        const data = await apiSendSignalToServer(formData);
        commit("SET_NEW_SIGNAL", data);
        commit("SORT_SIGNALS");
        commit("UPDATE_DRAFT_FORM", null);
        return data;
      } catch (err) {
        console.error("Ошибка addSignal:", err);
        return Promise.reject(err);
      } finally {
        commit("TOGGLE_SIGNAL_SEND_LOADING_FLAG", false);
      }
    },

    // -------------------------------
    // ИЗМЕНИТЬ СИГНАЛ
    // -------------------------------
    async changeSignal({ commit, state }, formData) {
      commit("TOGGLE_SIGNAL_SEND_LOADING_FLAG", true);

      try {
        const data = await apiSendChangedSignalToServer(
          formData,
          state.idEditSignal
        );
        commit("UPDATE_SIGNAL", data);
        return data;
      } catch (err) {
        console.error("Ошибка changeSignal:", err);
        return Promise.reject(err);
      } finally {
        commit("TOGGLE_SIGNAL_SEND_LOADING_FLAG", false);
      }
    },

    // -------------------------------
    // ОБНОВЛЕНИЕ ТРЕНДОВЫХ
    // -------------------------------
    async updateTrendingSignals({ commit, state }, data) {
      data.forEach((item) => {
        if (state.map.includes(item.id)) {
          commit("MAKE_SIGNAL_TRENDING", { id: item.id, status: true });
        } else {
          commit("SET_NEW_SIGNAL", item);
        }
      });

      commit("SORT_SIGNALS");
    },

    // -------------------------------
    // ОБНОВИТЬ ДАННЫЕ
    // -------------------------------
    async updateData({ commit }, data) {
      commit("UPDATE_DATA", data);
    },

    async saveDraftForm({ commit }, data) {
      commit("UPDATE_DRAFT_FORM", data);
    },

    async approveLiveStatus({ commit }, id) {
      commit("CHANGE_LIVE_STATUS", { id, status: true });
    },

    async updateIdEditSignal({ commit }, id) {
      commit("CHANGE_ID_EDIT_SIGNAL", id);
    },

    async refreshData({ commit }, data) {
      commit("REFRESH_DATA", data);
    },
  },

  // ===============================================================
  // MUTATIONS
  // ===============================================================
  mutations: {
    SET_DATA(state, { favorites, list }) {
      [...favorites, ...list].forEach((signal) => {
        if (!state.map.includes(signal.id)) {
          state.data[signal.id] = signal;
          state.map.push(signal.id);
        }
      });
    },

    SORT_SIGNALS(state) {
      state.map.sort((a, b) => {
        const A = state.data[a];
        const B = state.data[b];

        const prA = Number(A.pinned) * 2 + Number(A.trending);
        const prB = Number(B.pinned) * 2 + Number(B.trending);

        if (prA !== prB) return prB - prA;

        return (
          new Date(B.publication_date || B.created_at) -
          new Date(A.publication_date || A.created_at)
        );
      });
    },

    SET_BOOKMARK_STATE(state, { id, value }) {
      const s = state.data[id];
      if (!s) return;

      if (!s.reactions) s.reactions = {};
      if (!s.reactions.bookmarked) s.reactions.bookmarked = {};

      s.reactions.bookmarked.is_mine = value;
    },

    CLEAR_DATA(state) {
      state.data = {};
      state.map = [];
      state.next = null;
    },

    SET_NEW_SIGNAL(state, signal) {
      if (!state.map.includes(signal.id)) {
        state.data[signal.id] = signal;
        state.map.unshift(signal.id);
      }
    },

    UPDATE_DATA(state, data) {
      data.forEach((item) => {
        if (!(item.id in state.data)) {
          state.data[item.id] = item;
          state.map.unshift(item.id);
        }
      });
    },

    UPDATE_SIGNAL(state, upd) {
      const s = state.data[upd.id];
      if (!s) return;

      Object.assign(s, {
        title: upd.title,
        content: upd.content,
        link: upd.link,
        coin: upd.coin,
        images_list: upd.images_list,
      });
    },

    REFRESH_DATA(state, data) {
      data.forEach((item) => {
        const s = state.data[item.id];
        if (!s) return;

        Object.assign(s, {
          change_1d_usd: item.change_1d_usd,
          change_all_time_percent: item.change_all_time_percent,
          price_usd: item.price_usd,
          start_market_cap: item.start_market_cap,
          start_price_usd: item.start_price_usd,
          coin: item.coin,
          ath: item.ath,
          change_ath: item.change_ath,
          atl: item.atl,
          change_atl: item.change_atl,
        });
      });
    },

    SET_READY_FLAG(state) {
      state.isReady = true;
    },

    TOGGLE_LOADING_FLAG(state, val) {
      state.isLoading = val;
    },

    SET_NEW_LINK(state, link) {
      state.next = link;
    },

    TOGGLE_LOADING_NEW_PAGE_FLAG(state, val) {
      state.isLoadingNext = val;
    },

    SET_LAST_SIGNALS(state, data) {
      state.lastSignals = data;
    },

    SET_LOADING_LAST_SIGNALS(state, val) {
      state.isLoadingLastSignals = val;
    },

    SET_READY_LAST_SIGNALS(state, val) {
      state.readyLastSignals = val;
    },

    PIN_SIGNAL(state, id) {
      state.data[id].pinned = !state.data[id].pinned;
    },

    CHANGE_ID_EDIT_SIGNAL(state, id) {
      state.idEditSignal = id || null;
    },

    TOGGLE_SIGNAL_SEND_LOADING_FLAG(state, val) {
      state.isSignalSendLoading = val;
    },

    UPDATE_DRAFT_FORM(state, data) {
      state.draftForm = data;
    },

    CHANGE_LIVE_STATUS(state, { id, status }) {
      state.data[id].live = status;
    },

    MAKE_SIGNAL_TRENDING(state, { id, status }) {
      state.data[id].trending = status;
    },

    CHANGE_ACTIVE_FILTER(state, filter) {
      state.activeFilter = filter;
    },

    ALIGN_COMMENTS(state, id) {
      state.data[id].init_comments_count =
        state.data[id].comments_count;
    },

    UPDATE_COMMENTS_COUNTER(state, { id, count }) {
      const s = state.data[id];
      if (!s) return;

      s.comments_count = count;

      if (s.comments_count < s.init_comments_count) {
        s.init_comments_count = s.comments_count;
      }
    },

    SAVE_FAVORITES_COUNT_TO_STORAGE(state) {
      const count = state.map.filter(
        (id) => state.data[id]?.reactions?.bookmarked?.is_mine === true
      ).length;

      try {
        localStorage.setItem("signals_favorites_count", String(count));
      } catch (err) {
        console.warn("Не удалось сохранить избранные в LS:", err);
      }
    },
  },

  // ===============================================================
  // GETTERS
  // ===============================================================
  getters: {
    map: (state) => state.map,

    data: (state) => state.data,

    next: (state) => state.next,

    favoritesMap: (state) =>
      state.map.filter(
        (id) => state.data[id]?.reactions?.bookmarked?.is_mine === true
      ),

    usualSignalsMap: (state) =>
      state.map.filter(
        (id) => state.data[id]?.reactions?.bookmarked?.is_mine !== true
      ),

    isLoading: (state) => state.isLoading,
    isReady: (state) => state.isReady,
    isLoadingNext: (state) => state.isLoadingNext,
  },
};

export default Signals;